import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';

// Stable references — avoid creating new objects on every render
const CHART_MARGIN = { top: 10, right: 16, left: 8, bottom: 8 };
const X_TICK_STYLE = { fontSize: 12, fill: '#6B7280' };
const Y_TICK_STYLE = { fontSize: 11, fill: '#6B7280' };
const TOOLTIP_CURSOR = { fill: 'rgba(21, 106, 202, 0.06)' };
const TOOLTIP_CONTENT_STYLE = {
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  padding: '8px 12px',
  fontSize: 12,
};
const TOOLTIP_LABEL_STYLE = { fontWeight: 600, color: '#111827', marginBottom: 4 };
const BAR_RADIUS = [6, 6, 0, 0];
const EMPTY_BAR_FILL = '#E5E7EB';

const formatYTick = (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v);

function buildCells(data, color) {
  const cells = [];
  for (let i = 0; i < data.length; i += 1) {
    const fill = data[i].value > 0 ? color : EMPTY_BAR_FILL;
    cells.push(React.createElement(Cell, { key: `c-${i}`, fill }));
  }
  return cells;
}

/**
 * Monthly bar chart used inside Summary / Metric drill-down modals.
 *
 * Props:
 *  - data: [{ month, month_name, value }]
 *  - color: stroke/fill HEX (e.g. "#156ACA")
 *  - valueLabel: label for tooltip and Y-axis (e.g. "Production (MT)")
 *  - formatValue: optional fn(number) => string for tooltip & Y-axis ticks
 */
export const MonthlyBarChart = ({ data, color = '#156ACA', valueLabel, formatValue }) => {
  const fmt = formatValue || ((v) => (v?.toLocaleString ? v.toLocaleString() : v));
  const tooltipFormatter = (value) => [fmt(value), valueLabel || 'Value'];

  return (
    <div className="w-full h-64" data-testid="monthly-bar-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis dataKey="month_name" tick={X_TICK_STYLE} axisLine={false} tickLine={false} />
          <YAxis tick={Y_TICK_STYLE} tickFormatter={formatYTick} width={56} axisLine={false} tickLine={false} />
          <Tooltip
            cursor={TOOLTIP_CURSOR}
            contentStyle={TOOLTIP_CONTENT_STYLE}
            formatter={tooltipFormatter}
            labelStyle={TOOLTIP_LABEL_STYLE}
          />
          <Bar dataKey="value" radius={BAR_RADIUS} maxBarSize={48}>
            {buildCells(data, color)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MonthlyBarChart;
